import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Close, Delete, Gavel, Send } from '@mui/icons-material'
import BigModal from '../../../../../components/Modals/BigModal'
import API from '../../../../../services/API'

const NewComplexDecision = ({
  open,
  onClose,
  controlRoomID,
  linkedEventID,
}) => {
  const [problem, setProblem] = useState('')
  const [briefFacts, setBriefFacts] = useState([''])
  const [options, setOptions] = useState([
    { option: 'Do nothing', chosen: false, rationale: '' },
    { option: '', chosen: false, rationale: '' },
  ])
  const [finalDecision, setFinalDecision] = useState('')
  const [decisionRationale, setDecisionRationale] = useState('')
  const [actions, setActions] = useState([{ action: '', byWhen: '' }])
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setProblem('')
    setBriefFacts([''])
    setOptions([
      { option: 'Do nothing', chosen: false, rationale: '' },
      { option: '', chosen: false, rationale: '' },
    ])
    setFinalDecision('')
    setDecisionRationale('')
    setActions([{ action: '', byWhen: '' }])
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    const payload = {
      linkedControlRoom: controlRoomID,
      linkedEvent: linkedEventID,
      relatesTo: 'Control Room',
      problem,
      briefFacts: briefFacts.filter(Boolean).map((fact) => ({ fact })),
      optionsConsidered: options.filter((x) => x.option),
      finalDecision,
      decisionRationale,
      actions: actions.filter((x) => x.action),
    }

    try {
      const res = await API.post('/commandcontrol/complex-decision', payload)

      if (res.status === 200) {
        reset()
        onClose()
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BigModal open={open} onClose={onClose}>
      <Box>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 5,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <Gavel />
            </Box>

            <Stack flex={1}>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Command Decision Log
              </Typography>
              <Typography variant="h5" fontWeight={900} color="white">
                Record Complex Decision
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
                Capture facts, options, rationale and required actions.
              </Typography>
            </Stack>

            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Stack
          component="form"
          onSubmit={handleSubmit}
          spacing={2.5}
          padding={2}
        >
          <TextField
            label="Decision required / problem"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
          />

          <Divider />

          <Stack spacing={1}>
            <Typography fontWeight={900}>Brief facts</Typography>

            {briefFacts.map((fact, index) => (
              <Stack key={index} direction="row" spacing={1}>
                <TextField
                  label={`Fact ${index + 1}`}
                  value={fact}
                  onChange={(e) =>
                    setBriefFacts((prev) =>
                      prev.map((x, i) => (i === index ? e.target.value : x)),
                    )
                  }
                  fullWidth
                />

                <IconButton
                  onClick={() =>
                    setBriefFacts((prev) => prev.filter((_, i) => i !== index))
                  }
                  disabled={briefFacts.length === 1}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}

            <Button
              startIcon={<Add />}
              onClick={() => setBriefFacts((prev) => [...prev, ''])}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add fact
            </Button>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography fontWeight={900}>Options considered</Typography>

            {options.map((option, index) => (
              <Grid container spacing={1} key={index}>
                <Grid item xs={12} md={5}>
                  <TextField
                    label={`Option ${index + 1}`}
                    value={option.option}
                    onChange={(e) =>
                      setOptions((prev) =>
                        prev.map((x, i) =>
                          i === index ? { ...x, option: e.target.value } : x,
                        ),
                      )
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={5}>
                  <TextField
                    label="Rationale"
                    value={option.rationale}
                    onChange={(e) =>
                      setOptions((prev) =>
                        prev.map((x, i) =>
                          i === index ? { ...x, rationale: e.target.value } : x,
                        ),
                      )
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={8} md={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={option.chosen}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((x, i) => ({
                              ...x,
                              chosen: i === index ? e.target.checked : false,
                            })),
                          )
                        }
                      />
                    }
                    label="Chosen"
                  />
                </Grid>

                <Grid item xs={4} md={1}>
                  <IconButton
                    onClick={() =>
                      setOptions((prev) => prev.filter((_, i) => i !== index))
                    }
                    disabled={options.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              startIcon={<Add />}
              onClick={() =>
                setOptions((prev) => [
                  ...prev,
                  { option: '', chosen: false, rationale: '' },
                ])
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              Add option
            </Button>
          </Stack>

          <Divider />

          <TextField
            label="Final decision"
            value={finalDecision}
            onChange={(e) => setFinalDecision(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Decision rationale"
            value={decisionRationale}
            onChange={(e) => setDecisionRationale(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Divider />

          <Stack spacing={1}>
            <Typography fontWeight={900}>Actions required</Typography>

            {actions.map((action, index) => (
              <Grid container spacing={1} key={index}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label={`Action ${index + 1}`}
                    value={action.action}
                    onChange={(e) =>
                      setActions((prev) =>
                        prev.map((x, i) =>
                          i === index ? { ...x, action: e.target.value } : x,
                        ),
                      )
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={10} md={3}>
                  <TextField
                    label="By when"
                    type="datetime-local"
                    value={action.byWhen}
                    onChange={(e) =>
                      setActions((prev) =>
                        prev.map((x, i) =>
                          i === index ? { ...x, byWhen: e.target.value } : x,
                        ),
                      )
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={2} md={1}>
                  <IconButton
                    onClick={() =>
                      setActions((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              startIcon={<Add />}
              onClick={() =>
                setActions((prev) => [...prev, { action: '', byWhen: '' }])
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              Add action
            </Button>
          </Stack>

          <Alert severity="info" sx={{ borderRadius: 3 }}>
            This will also create an entry in the control room log.
          </Alert>

          <Button
            type="submit"
            variant="contained"
            disabled={!problem || !finalDecision || submitting}
            endIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Send />
              )
            }
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#239AA4',
              '&:hover': { backgroundColor: '#1E7F88' },
            }}
          >
            Record Complex Decision
          </Button>
        </Stack>
      </Box>
    </BigModal>
  )
}

export default NewComplexDecision
